// Resolving a phone ticket whose caller was never reached (2026-09-25).
//
// help@ 1a0cbbed7d72f3e9 was resolved with no reply at all: the caller's only
// address was unusable, the confirmation texts were blocked, and a click on
// Resolve made the ticket disappear. The api now refuses that resolve with a
// 409 `phone_caller_unreached` (email-recovery-route.ts resolveGuard) until the
// person says why. This is the console half: ask for the reason, send it back
// as `acknowledgeUnreached`, and leave the ticket open if they cancel.
//
// Pure apart from the two injected effects, so the flow is testable without a
// browser.

export type GuardedResolve =
  | { ok: true; ack?: string }
  | { ok: false; reason: 'cancelled' }
  | { ok: false; reason: 'failed'; message: string }

export interface ResolveGuardDeps {
  /** POST /api/support/status with this body. */
  post: (body: Record<string, unknown>) => Promise<Response>
  /** In-page confirmation: the typed reason, or null on Cancel. */
  ask: (message: string) => string | null | Promise<string | null>
}

export const UNREACHED_PROMPT_SUFFIX =
  '\n\nTo resolve anyway, explain why no further support action is needed. Cancel keeps the ticket open.'

async function failureMessage(res: Response): Promise<string> {
  let detail = ''
  try {
    const raw = await res.text()
    try {
      const body = JSON.parse(raw) as { error?: unknown; message?: unknown }
      const value = typeof body.message === 'string' ? body.message : body.error
      if (typeof value === 'string') detail = value.slice(0, 400)
    } catch { /* response was not JSON */ }
  } catch { /* response body unavailable */ }
  return detail || `Support status update failed (HTTP ${res.status}). Refresh the ticket to check its status before retrying.`
}

export async function resolveWithGuard(
  threadId: string,
  resolvedAt: string,
  deps: ResolveGuardDeps,
  /** Other status fields to send with the resolve (e.g. an Avery proposal's outcome). */
  extra: Record<string, unknown> = {},
): Promise<GuardedResolve> {
  const body = { ...extra, threadId, status: 'resolved', resolvedAt }
  let res: Response
  try {
    res = await deps.post(body)
  } catch {
    return { ok: false, reason: 'failed', message: 'Support status update was not confirmed. Refresh the ticket to check its status before retrying.' }
  }
  if (res.ok) return { ok: true }
  if (res.status !== 409) return { ok: false, reason: 'failed', message: await failureMessage(res) }
  const data = await res.json().catch(() => ({})) as { error?: string; message?: string }
  if (data.error !== 'phone_caller_unreached') {
    return { ok: false, reason: 'failed', message: data.message || data.error || 'Support status update was rejected (HTTP 409). The ticket was left unchanged.' }
  }
  let typed: string | null
  try {
    typed = await deps.ask((data.message ?? 'This phone caller has not been reached.') + UNREACHED_PROMPT_SUFFIX)
  } catch {
    return { ok: false, reason: 'failed', message: 'Could not open the resolve confirmation. The ticket has not been resolved.' }
  }
  const ack = (typed ?? '').trim()
  if (!ack) return { ok: false, reason: 'cancelled' }
  let retry: Response
  try {
    retry = await deps.post({ ...body, acknowledgeUnreached: ack })
  } catch {
    return { ok: false, reason: 'failed', message: 'Support status update was not confirmed. Refresh the ticket to check its status before retrying.' }
  }
  if (!retry.ok) return { ok: false, reason: 'failed', message: await failureMessage(retry) }
  return { ok: true, ack }
}
